import { Badge, ConfidenceBadge, VerificationBadge } from "@odingard/design-system";
import type { ProposalCitation, VerificationState } from "@odingard/sdk-client";

// Demo-mode banner. Rendered whenever the Worker answered from the deterministic
// fixture layer, so demo/simulated data is never mistaken for the live service.
export function DemoModeBanner({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <div className="ods-failclosed" role="status" style={{ borderColor: "var(--ods-color-warning)" }}>
      <strong>Demo data.</strong> Proposal AI is running against the deterministic fixture layer, not a live
      backend. Numbers and AI results below are illustrative.
    </div>
  );
}

// Live-mode chip: makes the real-backend path explicit in the UI so live and
// demo data are never confused.
export function LiveModeBadge({ demoMode }: { demoMode: boolean }) {
  if (demoMode) return null;
  return <Badge tone="success">Live backend</Badge>;
}

export interface GroundedMetaLike {
  provider?: string;
  promptVersion?: string;
  confidence?: number;
  citations?: ProposalCitation[];
}

// Grounded-AI evidence strip: provider, prompt version, confidence, verification
// state, and citation count. Every AI-derived result in Proposal AI renders this
// so users can see provenance and never treat AI output as verified fact.
export function GroundedEvidence({
  meta,
  verificationState,
}: {
  meta: GroundedMetaLike;
  verificationState?: VerificationState;
}) {
  return (
    <div className="ods-row" style={{ gap: "var(--ods-space-2)", flexWrap: "wrap", alignItems: "center" }}>
      {meta.provider ? <Badge tone="neutral">{meta.provider}</Badge> : null}
      {meta.promptVersion ? (
        <span style={{ color: "var(--ods-color-muted)", fontSize: "var(--ods-text-sm)" }}>{meta.promptVersion}</span>
      ) : null}
      {typeof meta.confidence === "number" ? <ConfidenceBadge value={meta.confidence} /> : null}
      {verificationState ? <VerificationBadge state={verificationState} /> : null}
      {meta.citations && meta.citations.length > 0 ? (
        <span style={{ fontSize: "var(--ods-text-sm)", color: "var(--ods-color-muted)" }}>
          {meta.citations.length} citation{meta.citations.length === 1 ? "" : "s"}
        </span>
      ) : null}
    </div>
  );
}

export function CitationList({ citations }: { citations: ProposalCitation[] }) {
  if (citations.length === 0) return null;
  return (
    <ul className="ods-stack" style={{ margin: "8px 0 0", paddingLeft: 0, listStyle: "none" }}>
      {citations.map((c, i) => (
        <li key={`${c.attachmentId ?? c.documentId ?? "src"}-${i}`} style={{ fontSize: "var(--ods-text-sm)" }}>
          <span className="ods-citation">
            <span aria-hidden="true">§</span> {c.attachmentId ?? c.documentId ?? "source"} · p.{c.page}
          </span>{" "}
          <span style={{ color: "var(--ods-color-muted)" }}>{c.snippet}</span>
        </li>
      ))}
    </ul>
  );
}
